import { useId } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Selector } from 'sonata-react-components';
import { DOOH_REPORT_SCREENS_PAGE_SIZE_OPTIONS } from 'sonata-static-storage/lib/entities/doohReport/doohReport.constants';

import { clampPage, getTotalPages } from '../deliveryDrivers.helpers';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';

import './listPagination.scss';

type Props = {
  id: string;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

/**
 * Local paginator footer of the Delivery Drivers screens list: a page-size `Selector`, the "x-y of n items" range, the page count and
 * prev/next icon buttons. Neither `TaptapList` nor `TableList` exposes a
 * standalone footer paginator, so this is built from existing pieces.
 */
export default function ListPagination({
  id,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const selectorId = useId();

  const totalPages = getTotalPages(totalItems, pageSize);
  const currentPage = clampPage(page, totalPages);
  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);

  const pageSizeOptions: SelectorOptionPropsInterface[] = DOOH_REPORT_SCREENS_PAGE_SIZE_OPTIONS.map(option => ({
    id: String(option),
    label: String(option)
  }));

  return (
    <div className="listPagination" id={id}>
      <div className="listPagination__pageSize">
        <Selector<SelectorOptionPropsInterface>
          id={selectorId}
          onChange={option => onPageSizeChange(Number(option.id))}
          options={pageSizeOptions}
          tiny
          type="simple"
          value={[pageSizeOptions.find(option => option.id === String(pageSize)) ?? pageSizeOptions[0]]}
        />
        <span className="listPagination__label">
          {formatMessage({
            defaultMessage: 'items per page',
            id: 'doohReport.deliveryDrivers.pagination.itemsPerPage'
          })}
        </span>
        <span className="listPagination__divider" />
        <span className="listPagination__label">
          {formatMessage(
            { defaultMessage: '{start}-{end} of {total} items', id: 'doohReport.deliveryDrivers.pagination.range' },
            { end: rangeEnd, start: rangeStart, total: totalItems }
          )}
        </span>
      </div>
      <div className="listPagination__pages">
        <span className="listPagination__label">
          {formatMessage(
            { defaultMessage: '{page} of {totalPages} pages', id: 'doohReport.deliveryDrivers.pagination.pages' },
            { page: currentPage, totalPages }
          )}
        </span>
        <ButtonUI
          className="listPagination__arrow"
          disabled={currentPage <= 1}
          icon="fa-solid fa-chevron-left"
          id={`${id}-prevPage`}
          onClick={() => onPageChange(currentPage - 1)}
          shape="link"
          size="small"
          type="secondary"
        />
        <ButtonUI
          className="listPagination__arrow"
          disabled={currentPage >= totalPages}
          icon="fa-solid fa-chevron-right"
          id={`${id}-nextPage`}
          onClick={() => onPageChange(currentPage + 1)}
          shape="link"
          size="small"
          type="secondary"
        />
      </div>
    </div>
  );
}
